import React, { useState, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { addJobRequest } from '../../../store/job/action';
import { toast } from 'react-toastify';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useTranslation } from 'react-i18next';
import {useNavigate} from "react-router-dom";

const NewJobs = () => {
  const { t } = useTranslation();
  document.title = t("new_jobs.title");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);

  const SingleOptions = [
    {
      options: [
        { value: "Accounting & Finance", label: t("new_jobs.form.category.accounting_finance") },
        { value: "Purchasing Manager", label: t("new_jobs.form.category.purchasing_manager") },
        { value: "Education & Training", label: t("new_jobs.form.category.education_training") },
        { value: "Marketing & Advertising", label: t("new_jobs.form.category.marketing_advertising") },
        { value: "IT / Software Jobs", label: t("new_jobs.form.category.it_software") },
        { value: "Digital Marketing", label: t("new_jobs.form.category.digital_marketing") },
        { value: "Administrative Officer", label: t("new_jobs.form.category.administrative_officer") },
        { value: "Government Jobs", label: t("new_jobs.form.category.government_jobs") },
      ],
    },
  ];

  const JobType = [
    {
      options: [
        { value: "Full-Time", label: t("new_jobs.form.job_type.full_time") },
        { value: "Part-Time", label: t("new_jobs.form.job_type.part_time") },
        { value: "Internship", label: t("new_jobs.form.job_type.internship") },
        { value: "Contract", label: t("new_jobs.form.job_type.contract") },
      ],
    },
  ];

  const CompanyOptions = [
    {
      options: [
        { value: "Confidoo", label: t("new_jobs.form.company.confidoo") },
        { value: "Tagstream", label: t("new_jobs.form.company.tagstream") },
        { value: "RFIDTunisie", label: t("new_jobs.form.company.rfidtunisie") },
      ],
    },
  ];

  const validation = useFormik({
    initialValues: {
      jobTitle: '',
      jobPosition: '',
      jobCategory: '',
      jobType: '',
      description: '',
      startSalary: '',
      lastSalary: '',
      country: '',
      state: '',
      lastDateApply: '',
      closeDate: '',
      tags: '',
      company: '',
    },
    validationSchema: Yup.object({
      jobTitle: Yup.string().required(t('new_jobs.form.job_title_required')),
      jobPosition: Yup.string().required(t('new_jobs.form.job_position_required')),
      jobCategory: Yup.string().required(t('new_jobs.form.job_category_required')),
      jobType: Yup.string().oneOf(['Full-Time', 'Part-Time', 'Internship', 'Contract']).required(t('new_jobs.form.job_type_required')),
      description: Yup.string().required(t('new_jobs.form.description_required')),
      startSalary: Yup.number().min(0, t('new_jobs.form.salary_positive')).optional(),
      lastSalary: Yup.number().min(0, t('new_jobs.form.salary_positive')).optional(),
      country: Yup.string().required(t('new_jobs.form.country_required')),
      state: Yup.string().required(t('new_jobs.form.state_required')),
      lastDateApply: Yup.date().required(t('new_jobs.form.last_date_apply_required')),
      closeDate: Yup.date().required(t('new_jobs.form.close_date_required')),
      tags: Yup.string().optional(),
      company: Yup.string().oneOf(['Confidoo', 'Tagstream', 'RFIDTunisie']).required(t('new_jobs.form.company_required')),
    }),
    onSubmit: (values) => {
      const jobData = {
        titre: values.jobTitle,
        position: values.jobPosition,
        type: values.jobType,
        category: values.jobCategory,
        startSalary: values.startSalary || undefined,
        lastSalary: values.lastSalary || undefined,
        country: values.country,
        state: values.state,
        tags: tags.length > 0 ? tags : undefined,
        lastDateApply: values.lastDateApply,
        closeDate: values.closeDate,
        description: values.description,
        company: values.company,
      };
        console.log("Submitting job data:", jobData);
      dispatch(addJobRequest(jobData));
      // validation.resetForm();
      setTags([]);
      toast.success(t("new_jobs.toasts.add_success"));
      navigate('/apps-job-lists');
    },
  });

  const handleTagsChange = (e) => {
    const value = e.target.value;
    setTags(value.split(',').map(tag => tag.trim()).filter(tag => tag));
    validation.setFieldValue('tags', value);
  };

  return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title={t("new_jobs.title")} pageTitle={t("new_jobs.page_title")} />
            <Row>
              <Col lg={12}>
                <Card>
                  <Form onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}>
                    <CardHeader>
                      <h5 className="card-title mb-0">{t("new_jobs.card_title")}</h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="g-4">
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="job-title-Input" className="form-label">
                              {t("new_jobs.form.job_title")} <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="text"
                                className="form-control"
                                id="job-title-Input"
                                placeholder={t("new_jobs.form.job_title_placeholder")}
                                name="jobTitle"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.jobTitle}
                                invalid={validation.touched.jobTitle && validation.errors.jobTitle}
                            />
                            {validation.touched.jobTitle && validation.errors.jobTitle && (
                                <div className="invalid-feedback">{validation.errors.jobTitle}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="job-position-Input" className="form-label">
                              {t("new_jobs.form.job_position")} <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="text"
                                className="form-control"
                                id="job-position-Input"
                                placeholder={t("new_jobs.form.job_position_placeholder")}
                                name="jobPosition"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.jobPosition}
                                invalid={validation.touched.jobPosition && validation.errors.jobPosition}
                            />
                            {validation.touched.jobPosition && validation.errors.jobPosition && (
                                <div className="invalid-feedback">{validation.errors.jobPosition}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="job-category-Input" className="form-label">
                              {t("new_jobs.form.job_category")} <span className="text-danger">*</span>
                            </Label>
                            <Select
                                id="job-category-Input"
                                name="jobCategory"
                                options={SingleOptions}
                                onChange={(option) => validation.setFieldValue('jobCategory', option.value)}
                                onBlur={() => validation.setFieldTouched('jobCategory', true)}
                                value={SingleOptions[0].options.find(opt => opt.value === validation.values.jobCategory)}
                                className={validation.touched.jobCategory && validation.errors.jobCategory ? 'is-invalid' : ''}
                            />
                            {validation.touched.jobCategory && validation.errors.jobCategory && (
                                <div className="invalid-feedback">{validation.errors.jobCategory}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="job-type-Input" className="form-label">
                              {t("new_jobs.form.job_type")} <span className="text-danger">*</span>
                            </Label>
                            <Select
                                id="job-type-Input"
                                name="jobType"
                                options={JobType}
                                onChange={(option) => validation.setFieldValue('jobType', option.value)}
                                onBlur={() => validation.setFieldTouched('jobType', true)}
                                value={JobType[0].options.find(opt => opt.value === validation.values.jobType)}
                                className={validation.touched.jobType && validation.errors.jobType ? 'is-invalid' : ''}
                            />
                            {validation.touched.jobType && validation.errors.jobType && (
                                <div className="invalid-feedback">{validation.errors.jobType}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="company-Input" className="form-label">
                              {t("new_jobs.form.company")} <span className="text-danger">*</span>
                            </Label>
                            <Select
                                id="company-Input"
                                name="company"
                                options={CompanyOptions}
                                onChange={(option) => validation.setFieldValue('company', option.value)}
                                onBlur={() => validation.setFieldTouched('company', true)}
                                value={CompanyOptions[0].options.find(opt => opt.value === validation.values.company)}
                                className={validation.touched.company && validation.errors.company ? 'is-invalid' : ''}
                            />
                            {validation.touched.company && validation.errors.company && (
                                <div className="invalid-feedback">{validation.errors.company}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div>
                            <Label htmlFor="description-field" className="form-label">
                              {t("new_jobs.form.description")} <span className="text-danger">*</span>
                            </Label>
                            <CKEditor
                                editor={ClassicEditor}
                                data={validation.values.description}
                                onChange={(event, editor) => {
                                  const data = editor.getData();
                                  validation.setFieldValue('description', data);
                                }}
                                onBlur={() => validation.setFieldTouched('description', true)}
                                className={validation.touched.description && validation.errors.description ? 'is-invalid' : ''}
                            />
                            {validation.touched.description && validation.errors.description && (
                                <div className="invalid-feedback d-block">{validation.errors.description}</div>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div>
                            <Label htmlFor="start-salary-Input" className="form-label">
                              {t("new_jobs.form.start_salary")}
                            </Label>
                            <Input
                                type="number"
                                className="form-control"
                                id="start-salary-Input"
                                placeholder={t("new_jobs.form.start_salary_placeholder")}
                                name="startSalary"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.startSalary}
                                invalid={validation.touched.startSalary && validation.errors.startSalary}
                            />
                            {validation.touched.startSalary && validation.errors.startSalary && (
                                <div className="invalid-feedback">{validation.errors.startSalary}</div>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div>
                            <Label htmlFor="last-salary-Input" className="form-label">
                              {t("new_jobs.form.last_salary")}
                            </Label>
                            <Input
                                type="number"
                                className="form-control"
                                id="last-salary-Input"
                                placeholder={t("new_jobs.form.last_salary_placeholder")}
                                name="lastSalary"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.lastSalary}
                                invalid={validation.touched.lastSalary && validation.errors.lastSalary}
                            />
                            {validation.touched.lastSalary && validation.errors.lastSalary && (
                                <div className="invalid-feedback">{validation.errors.lastSalary}</div>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div>
                            <Label htmlFor="country-Input" className="form-label">
                              {t("new_jobs.form.country")} <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="text"
                                className="form-control"
                                id="country-Input"
                                placeholder={t("new_jobs.form.country_placeholder")}
                                name="country"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.country}
                                invalid={validation.touched.country && validation.errors.country}
                            />
                            {validation.touched.country && validation.errors.country && (
                                <div className="invalid-feedback">{validation.errors.country}</div>
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div>
                            <Label htmlFor="state-Input" className="form-label">
                              {t("new_jobs.form.state")} <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="text"
                                className="form-control"
                                id="state-Input"
                                placeholder={t("new_jobs.form.state_placeholder")}
                                name="state"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.state}
                                invalid={validation.touched.state && validation.errors.state}
                            />
                            {validation.touched.state && validation.errors.state && (
                                <div className="invalid-feedback">{validation.errors.state}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="last-apply-date-Input" className="form-label">
                              {t("new_jobs.form.last_date_apply")} <span className="text-danger">*</span>
                            </Label>
                            <Flatpickr
                                className="form-control"
                                id="last-apply-date-Input"
                                placeholder={t("new_jobs.form.date_placeholder")}
                                options={{
                                  altInput: true,
                                  altFormat: "F j, Y",
                                  dateFormat: "Y-m-d",
                                }}
                                value={validation.values.lastDateApply}
                                onChange={([date]) => validation.setFieldValue('lastDateApply', date)}
                                onBlur={() => validation.setFieldTouched('lastDateApply', true)}
                            />
                            {validation.touched.lastDateApply && validation.errors.lastDateApply && (
                                <div className="invalid-feedback d-block">{validation.errors.lastDateApply}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div>
                            <Label htmlFor="close-date-Input" className="form-label">
                              {t("new_jobs.form.close_date")} <span className="text-danger">*</span>
                            </Label>
                            <Flatpickr
                                className="form-control"
                                id="close-date-Input"
                                placeholder={t("new_jobs.form.date_placeholder")}
                                options={{
                                  altInput: true,
                                  altFormat: "F j, Y",
                                  dateFormat: "Y-m-d",
                                }}
                                value={validation.values.closeDate}
                                onChange={([date]) => validation.setFieldValue('closeDate', date)}
                                onBlur={() => validation.setFieldTouched('closeDate', true)}
                            />
                            {validation.touched.closeDate && validation.errors.closeDate && (
                                <div className="invalid-feedback d-block">{validation.errors.closeDate}</div>
                            )}
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div>
                            <Label htmlFor="tags-field" className="form-label">
                              {t("new_jobs.form.tags")}
                            </Label>
                            <Input
                                type="text"
                                className="form-control"
                                id="tags-field"
                                placeholder={t("new_jobs.form.tags_placeholder")}
                                name="tags"
                                onChange={handleTagsChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.tags}
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="hstack justify-content-end gap-2">
                            <Button
                                type="button"
                                className="btn bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-300"
                                onClick={() => {
                                  validation.resetForm();
                                  setTags([]);
                                }}
                            >
                              <i className="ri-close-line align-bottom"></i> {t("new_jobs.form.reset")}
                            </Button>
                            <Button type="submit" className="btn bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition duration-300">
                              {t("new_jobs.form.add_job")}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Form>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
  );
};

export default NewJobs;